import { useMultiEmailInput } from '@consigli/hooks';
import clsx from 'clsx';
import { HiOutlineX } from 'react-icons/hi';

import { Label } from '@/components';

export interface MultiEmailInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Per-email validator. Return a non-empty error string to reject. */
  validateEmail?: (email: string, current: string[]) => string | undefined;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Aggregate form-level error. Shown only when there are no chips. */
  formError?: string;
}

export const MultiEmailInput = ({
  value,
  onChange,
  validateEmail,
  label,
  placeholder = 'Type an email and press Enter',
  disabled = false,
  formError,
}: MultiEmailInputProps) => {
  const { draft, draftError, onDraftChange, onKeyDown, onBlur, onPaste, removeAt } =
    useMultiEmailInput({ value, onChange, validateEmail });

  const showFormError = !draftError && Boolean(formError) && value.length === 0;
  const hasError = Boolean(draftError) || showFormError;

  return (
    <Label text={label} className="w-full">
      <div
        className={clsx(
          'flex flex-wrap items-center gap-2 min-h-[40px] w-full rounded-md border bg-white px-2 py-1 text-sm',
          'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
          'transition-[color,box-shadow]',
          {
            'border-red-500': hasError,
            'cursor-not-allowed opacity-50': disabled,
          },
        )}
      >
        {value.map((email, index) => (
          <span
            key={email}
            data-testid="recipient-chip"
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs"
          >
            {email}
            <button
              type="button"
              aria-label={`Remove ${email}`}
              disabled={disabled}
              onClick={() => removeAt(index)}
              className="text-gray-500 hover:text-gray-900"
            >
              <HiOutlineX className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          id="recipients-input"
          type="email"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onPaste={onPaste}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          aria-label={label ?? 'Recipients'}
          aria-invalid={hasError}
          className="flex-1 min-w-[180px] bg-transparent outline-none placeholder:text-muted-foreground"
        />
      </div>
      {(draftError || showFormError) && (
        <p role="alert" className="mt-1 text-sm text-red-600">
          {draftError ?? formError}
        </p>
      )}
    </Label>
  );
};
